import { parse } from 'date-fns';
import React, { useState, useRef, useEffect } from 'react';

import { months, getDaysInMonth, formatDate } from '@/utils/formValidation/dateUtils';

type DatePickerProps = {
  value: string; // ISO date string (YYYY-MM-DD) or empty string
  onChange: (date: string) => void; // Called with ISO date string (YYYY-MM-DD)
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  label?: string; // Optional label for the date (e.g., "Date of Birth" or "Date of Incorporation")
};

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Date of Birth',
  error = false,
  disabled = false,
  label,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [viewMode, setViewMode] = useState<'days' | 'months' | 'years'>('days');
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Generate years going back 100 years from current year
  const getYearList = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 101 }, (_, i) => currentYear - i);
  };

  const years = getYearList();

  // Initialize date picker from value
  useEffect(() => {
    if (value) {
      const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
      if (!isNaN(parsedDate.getTime())) {
        setSelectedMonth(months[parsedDate.getMonth()]);
        setSelectedYear(parsedDate.getFullYear());
        setSelectedDate(parsedDate.getDate());
        setCurrentMonth(parsedDate.getMonth());
        setCurrentYear(parsedDate.getFullYear());
      }
    } else {
      // Reset to current date if no value
      const now = new Date();
      setSelectedMonth('');
      setSelectedYear(now.getFullYear());
      setSelectedDate(null);
      setCurrentMonth(now.getMonth());
      setCurrentYear(now.getFullYear());
    }
    // Reset view mode when picker opens/closes
    if (!showDatePicker) {
      setViewMode('days');
    }
  }, [value, showDatePicker]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const handleDateSelect = (day: number) => {
    setSelectedDate(day);
    setSelectedMonth(months[currentMonth]);
    setSelectedYear(currentYear);
    const dateStr = formatDate(day, currentMonth, currentYear);
    onChange(dateStr);
    setShowDatePicker(false);
  };

  const handleYearSelect = (year: number) => {
    setCurrentYear(year);
    setSelectedYear(year);
    setViewMode('months');
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(monthIndex);
    setSelectedMonth(months[monthIndex]);
    setViewMode('days');
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const days: React.ReactNode[] = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          style={{
            width: 36,
            height: 36,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate === day && selectedMonth === months[currentMonth] && selectedYear === currentYear;
      days.push(
        <div
          key={day}
          onClick={() => handleDateSelect(day)}
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: isSelected ? '#113D7B' : 'transparent',
            color: isSelected ? 'white' : '#101828',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: 14,
            fontFamily: 'var(--ep-font-avenir)',
            fontWeight: isSelected ? 600 : 400,
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              (e.currentTarget as HTMLDivElement).style.backgroundColor = '#F4F4F4';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          {day}
        </div>
      );
    }

    // Group days into weeks
    const weeks: React.ReactNode[] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(
        <div
          key={`week-${i}`}
          style={{
            alignSelf: 'stretch',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 4,
            display: 'inline-flex',
          }}
        >
          {days.slice(i, i + 7)}
        </div>
      );
    }

    return weeks;
  };

  const displayDate = value ? `${selectedMonth} ${selectedDate}, ${selectedYear}` : label || placeholder;

  return (
    <div
      ref={datePickerRef}
      style={{
        width: '100%',
        height: 43,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 12,
        paddingBottom: 12,
        background: '#F4F4F4',
        borderRadius: 8,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 4,
        display: 'inline-flex',
        position: 'relative',
        outline: error ? '1px var(--Error, #CC4747) solid' : undefined,
        outlineOffset: error ? '-1px' : undefined,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div
        onClick={() => !disabled && setShowDatePicker(!showDatePicker)}
        style={{
          flex: '1 1 0',
          color: value ? 'black' : '#B2B2B2',
          fontSize: 14,
          fontFamily: 'var(--ep-font-avenir)',
          fontWeight: 500,
          wordWrap: 'break-word',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {displayDate}
      </div>
      <div data-icon="ic:arrowdown" style={{ width: 16, height: 16, position: 'relative', overflow: 'hidden' }} />
      {showDatePicker && !disabled && (
        <div
          style={{
            width: 288,
            padding: 20,
            left: 35,
            position: 'absolute',
            background: 'white',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            borderRadius: 12,
            outline: '1px #E5E7EB solid',
            outlineOffset: '-1px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 16,
            display: 'inline-flex',
            zIndex: 1000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with navigation - only show for days view */}
          {viewMode === 'days' && (
            <div
              style={{
                alignSelf: 'stretch',
                overflow: 'hidden',
                justifyContent: 'space-between',
                alignItems: 'center',
                display: 'inline-flex',
              }}
            >
              <button
                onClick={() => navigateMonth('prev')}
                style={{
                  width: 24,
                  height: 24,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/weui-arrow-filled-left.svg" alt="Previous month" style={{ width: '24px', height: '24px' }} />
              </button>
              <div
                style={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 4,
                  display: 'flex',
                }}
              >
                <div
                  onClick={() => setViewMode('months')}
                  style={{
                    textAlign: 'center',
                    color: '#101828',
                    fontSize: 14,
                    fontFamily: 'var(--ep-font-avenir)',
                    fontWeight: 500,
                    lineHeight: 1.43,
                    wordWrap: 'break-word',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = '#F4F4F4';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                  }}
                >
                  {months[currentMonth]}
                </div>
                <div
                  onClick={() => setViewMode('years')}
                  style={{
                    textAlign: 'center',
                    color: '#101828',
                    fontSize: 14,
                    fontFamily: 'var(--ep-font-avenir)',
                    fontWeight: 500,
                    lineHeight: 1.43,
                    wordWrap: 'break-word',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = '#F4F4F4';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                  }}
                >
                  {currentYear}
                </div>
              </div>
              <button
                onClick={() => navigateMonth('next')}
                style={{
                  width: 24,
                  height: 24,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/weui-arrow-filled-right.svg" alt="Next month" style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
          )}

          {/* Year Picker View */}
          {viewMode === 'years' && (
            <div
              style={{
                alignSelf: 'stretch',
                flexDirection: 'column',
                gap: 8,
                display: 'flex',
              }}
            >
              <div
                style={{
                  textAlign: 'center',
                  color: '#101828',
                  fontSize: 16,
                  fontFamily: 'var(--ep-font-avenir)',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                Select Year
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 8,
                  maxHeight: 240,
                  overflowY: 'auto',
                  padding: '8px 0',
                }}
              >
                {years.map((year) => (
                  <div
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    style={{
                      padding: '8px 4px',
                      textAlign: 'center',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontFamily: 'var(--ep-font-avenir)',
                      fontWeight: year === currentYear ? 600 : 400,
                      color: year === currentYear ? '#113D7B' : '#101828',
                      background: year === currentYear ? '#E5E7EB' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (year !== currentYear) {
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = '#F4F4F4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (year !== currentYear) {
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Month Picker View */}
          {viewMode === 'months' && (
            <div
              style={{
                alignSelf: 'stretch',
                flexDirection: 'column',
                gap: 8,
                display: 'flex',
              }}
            >
              <div
                style={{
                  textAlign: 'center',
                  color: '#101828',
                  fontSize: 16,
                  fontFamily: 'var(--ep-font-avenir)',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                Select Month
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                  padding: '8px 0',
                }}
              >
                {months.map((month, index) => (
                  <div
                    key={month}
                    onClick={() => handleMonthSelect(index)}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontFamily: 'var(--ep-font-avenir)',
                      fontWeight: index === currentMonth ? 600 : 400,
                      color: index === currentMonth ? '#113D7B' : '#101828',
                      background: index === currentMonth ? '#E5E7EB' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (index !== currentMonth) {
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = '#F4F4F4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (index !== currentMonth) {
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {month.slice(0, 3)}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Days View - Calendar */}
          {viewMode === 'days' && (
            <div
              style={{
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 10,
                display: 'flex',
              }}
            >
              <div
                style={{
                  width: 252,
                  overflow: 'hidden',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  display: 'inline-flex',
                }}
              >
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div
                    key={day}
                    style={{
                      flex: '1 1 0',
                      textAlign: 'center',
                      color: '#4A5565',
                      fontSize: 12,
                      fontFamily: 'var(--ep-font-avenir)',
                      fontWeight: 500,
                    }}
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div
                style={{
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  display: 'flex',
                }}
              >
                {renderCalendarDays()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
